import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'lib/utils';
import { Slot } from 'radix-ui';
import * as React from 'react';

/**
 * A feedback banner. Each tone pairs `{x}-muted` (background), `{x}-border`
 * and `{x}-text`, as the Figma variables intend (`feedback/{x}/bg`, `/border`,
 * `/fg` in the old token names).
 *
 * Two neutral variants carry information rather than feedback: `elevated` is
 * the Figma "Info alert" (a white card with a light shadow, often with an
 * `AlertMedia`), `outline` is the Figma "Card Info" (a bordered note on the
 * page background).
 *
 * An `AlertAction` (a button) sits in a third column on the right.
 */
const alertVariants = cva(
  // An Ant Design icon is a span wrapping the svg, so it gets the same icon column as a bare svg.
  'relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>.anticon]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>[data-slot=alert-media]]:grid-cols-[calc(var(--spacing)*10)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 has-[>.anticon]:gap-x-3 has-[>[data-slot=alert-media]]:gap-x-3 has-[>[data-slot=alert-action]]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current [&>.anticon]:size-4 [&>.anticon]:translate-y-0.5 [&>.anticon]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        info: 'bg-info-muted border-info-border text-info-text',
        success: 'bg-success-muted border-success-border text-success-text',
        warning: 'bg-warning-muted border-warning-border text-warning-text',
        destructive: 'bg-destructive-muted border-destructive-border text-destructive-text',
        elevated:
          'bg-card text-card-foreground border-transparent p-4 shadow-xs *:data-[slot=alert-title]:text-base *:data-[slot=alert-description]:text-muted-foreground',
        outline: 'bg-transparent text-foreground p-4 items-center [&>svg]:size-5 [&>svg]:translate-y-0',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Alert({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants> & { asChild?: boolean }) {
  // An alert that opens something renders as its button (`asChild`), without the alert role
  const Comp = asChild ? Slot.Root : 'div';
  return (
    <Comp
      data-slot="alert"
      data-variant={variant ?? 'default'}
      role={asChild ? undefined : 'alert'}
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight', className)}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      // Block flow: inline text, <b> and links wrap naturally; paragraphs get a gap
      className={cn('col-start-2 space-y-1 text-sm [&_p]:leading-relaxed', className)}
      {...props}
    />
  );
}

/** A round medallion holding the alert's icon, in place of a bare icon (Figma "Info alert"). */
function AlertMedia({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="alert-media"
      className={cn(
        'row-span-2 flex size-10 items-center justify-center rounded-full bg-primary/50 text-foreground [&>svg]:size-5',
        className,
      )}
      {...props}
    />
  );
}

/** A button on the right of the alert, across its title and description. */
function AlertAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-action"
      className={cn('col-start-3 row-span-2 row-start-1 self-center', className)}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription, AlertMedia, AlertAction };
